import {
  Box,
  FieldSelect,
  FlexBox,
  HelperField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import { Component, ComponentType } from 'react';
import { Field, FieldArray, InjectedFormProps } from 'redux-form';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import {
  FlowManagerEditModalComponentT,
  FlowType,
  NodeT,
} from 'fable/flowEditor';
import { AutomationNodeTypes, AutomationTriggerHumanNames } from 'hocs';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import { getConditionHeadCaption, getConditionHeadTitle } from '../constants';
import {
  ConditionFormValuesT,
  MatchingOperator,
  MatchingStrategy,
  MatchingSystemType,
  MatchVariableT,
  VariableScope,
} from '../nodeTypes/types';
import type { AutomationFlowExtraDataT } from '../types';

import type { ConditionVariablesT } from './condition/conditionVariableSelector';
import { FieldConditionComponent } from './condition/fieldConditionComponent';

export type ConditionModalStatePropsT = {
  initialValues: ConditionFormValuesT;
} & ConditionFormValuesT;

export type ConditionModalOwnPropsT<TNode extends NodeT> = {
  type: TNode['nodeType'];
} & FlowManagerEditModalComponentT<TNode, AutomationFlowExtraDataT>;

export type ConditionModalDispatchPropsT = {};

export type ConditionModalPropsT<TNode extends NodeT> =
  ConditionModalOwnPropsT<TNode> &
    ConditionModalStatePropsT &
    ConditionModalDispatchPropsT;

type ConditionModalInjectedProps<TNode extends NodeT> = InjectedFormProps<
  ConditionFormValuesT,
  ConditionModalPropsT<TNode>
>;

type PropsT<TNode extends NodeT> = ConditionModalPropsT<TNode> &
  ConditionModalInjectedProps<TNode>;

type ValidationT = {
  rules?: string;
};

enum FormStatus {
  Initial = 'init',
  Mounting = 'mounting',
  Mounted = 'mounted',
}

type StateT<TNode extends NodeT> = {
  validation: ValidationT;
  formStatus: FormStatus;
} & {
  payload: TNode['payload'];
};

export const createConditionModal = <TNode extends NodeT>(
  type: AutomationNodeTypes.INIT | AutomationNodeTypes.CONDITION,
  createInitialPayload: (
    node: ConditionModalPropsT<TNode>['node'],
  ) => TNode['payload'],
): ComponentType<PropsT<TNode>> => {
  return class ConditionModal extends Component<PropsT<TNode>, StateT<TNode>> {
    constructor(props: PropsT<TNode>) {
      super(props);
      const { node } = props;

      this.state = {
        validation: {},
        payload: createInitialPayload(node),
        formStatus: FormStatus.Initial,
      };

      this.onFormMounted = this.onFormMounted.bind(this);
      this.handleConfirm = this.handleConfirm.bind(this);
      this.closeModal = this.closeModal.bind(this);
    }

    componentDidMount() {
      setTimeout(() => {
        this.setState({
          formStatus: FormStatus.Mounting,
        });
      });
    }

    onFormMounted() {
      this.setState({
        formStatus: FormStatus.Mounted,
      });
    }

    getAvailableFlowVariables(): ConditionVariablesT {
      const flowCategories: ConditionVariablesT = [];

      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: 'Tags',
            item: {
              scope: VariableScope.SYSTEM,
              variableType: MatchingSystemType.TAGS,
            },
          },
          {
            name: 'Queue',
            item: {
              scope: VariableScope.SYSTEM,
              variableType: MatchingSystemType.QUEUE,
            },
          },
          {
            name: 'Status',
            item: {
              scope: VariableScope.SYSTEM,
              variableType: MatchingSystemType.STATUS,
            },
          },
          {
            name: 'Language',
            item: {
              scope: VariableScope.SYSTEM,
              variableType: MatchingSystemType.LANGUAGE,
            },
          },
          {
            name: 'Agent has replied',
            item: {
              scope: VariableScope.SYSTEM,
              variableType: MatchingSystemType.AGENT_HAS_REPLIED,
            },
          },
          {
            name: 'Customer has replied',
            item: {
              scope: VariableScope.SYSTEM,
              variableType: MatchingSystemType.USER_HAS_REPLIED,
            },
          },
          ...(type === AutomationNodeTypes.CONDITION
            ? [
                {
                  name: 'Agent has replied since trigger',
                  item: {
                    scope: VariableScope.SYSTEM,
                    variableType: MatchingSystemType.AGENT_HAS_REPLIED_TRIGGER,
                  },
                },
                {
                  name: 'Customer has replied since trigger',
                  item: {
                    scope: VariableScope.SYSTEM,
                    variableType: MatchingSystemType.USER_HAS_REPLIED_TRIGGER,
                  },
                },
              ]
            : []),
        ],
      });

      return flowCategories;
    }

    handleConfirm(): void {
      const errors = this.validate();
      if (Object.keys(errors).length > 0) {
        this.setState({ validation: errors });
        return;
      }

      this.saveNode();
    }

    handleAnalytics(): void {
      const { node, rules, matchingStrategy } = this.props;
      const { payload } = this.state;

      const rulesDifference = differenceWith(rules, payload.rules, isEqual).map(
        ({ match, operator, variable }) => ({
          variableType: variable.variableType,
          operator,
          ...([
            MatchingSystemType.STATUS,
            MatchingSystemType.LANGUAGE,
            MatchingSystemType.AGENT_HAS_REPLIED,
            MatchingSystemType.USER_HAS_REPLIED,
          ].includes(variable.variableType as MatchingSystemType) && {
            match: match.values.map(({ value }) => value),
          }),
        }),
      );

      const settings = {
        ...(rulesDifference.length && { rules: rulesDifference }),
        ...(matchingStrategy !== payload.matchingStrategy && {
          matchingStrategy,
        }),
      };

      trackFlowActionSaved(!!node.isAction, {
        flowType: FlowType.FLOW_TYPE_AUTOMATION,
        nodeType: type,
        ...(Object.keys(settings).length && { settings }),
      });
    }

    saveNode() {
      const { node, replaceNode, rules, matchingStrategy } = this.props;
      const { payload } = this.state;

      this.handleAnalytics();

      const newNode = {
        ...node,
        nodeType: type,
        isAction: false,
        isRemovable: type !== AutomationNodeTypes.INIT,
        isEditable: true,
        payload: {
          ...payload,
          matchingStrategy,
          rules,
        },
      } as TNode;

      replaceNode(newNode);
      this.closeModal();
    }

    closeModal() {
      this.props.triggerClose();
    }

    isRuleOperatorSpecific(operator: MatchingOperator) {
      return (
        operator !== MatchingOperator.IS_EMPTY &&
        operator !== MatchingOperator.IS_NOT_EMPTY
      );
    }

    validate() {
      const errors: ValidationT = {};
      const { rules } = this.props;

      if (rules.length < 1) {
        errors.rules = 'Please add at least 1 condition';
      }

      if (!errors.rules && type === AutomationNodeTypes.INIT) {
        const foundQueueOrTagRule = rules.find((rule) => {
          const tagOrQueue: string[] = [
            MatchingSystemType.QUEUE,
            MatchingSystemType.TAGS,
          ];
          return (
            tagOrQueue.includes(rule.variable.variableType) &&
            this.isRuleOperatorSpecific(rule.operator)
          );
        });
        if (!foundQueueOrTagRule) {
          errors.rules =
            'Please add at least 1 condition filtering by a queue or tag';
        }
      }

      if (!errors.rules) {
        rules.find((rule) => {
          const ruleIsEmpty = !rule.variable || !rule.operator || !rule.match;
          if (ruleIsEmpty) {
            errors.rules = 'Please fill in empty conditions';
          }
        });
      }

      if (!errors.rules) {
        rules.find((rule) => {
          const ruleHasMatchesAssigned = rule.match.values.length > 0;
          const needsMatch =
            rule.operator !== MatchingOperator.IS_EMPTY &&
            rule.operator !== MatchingOperator.IS_NOT_EMPTY;
          if (
            !ruleHasMatchesAssigned &&
            needsMatch &&
            (
              [
                MatchingSystemType.QUEUE,
                MatchingSystemType.TAGS,
                MatchingSystemType.STATUS,
                MatchingSystemType.LANGUAGE,
              ] as MatchVariableT['variableType'][]
            ).includes(rule.variable.variableType)
          ) {
            errors.rules = 'Please add at least 1 match to your rules';
          }
        });
      }

      return errors;
    }

    saveClickHandler(values: ConditionFormValuesT) {
      this.saveNode();
    }

    handleAddRow() {
      ampli.flowActionConditionAdded({
        flowType: FlowType.FLOW_TYPE_AUTOMATION,
      });
    }

    render() {
      const { node, extraData, matchingStrategy, rules } = this.props;
      const { validation, formStatus } = this.state;
      const { payload } = node;

      const isChanged =
        payload?.matchingStrategy !== matchingStrategy ||
        payload?.rules.length !== rules.length ||
        rules.some((rule, index) => !isEqual(rule, payload?.rules[index]));

      return (
        <Box width="600px">
          <ModalHead
            title={getConditionHeadTitle(type)}
            onClose={this.closeModal}
          >
            {getConditionHeadCaption(type)}
            {type === AutomationNodeTypes.INIT && (
              <Box
                background="blue.100"
                borderRadius
                padding="300"
                margin="300 0 0"
              >
                <strong>Trigger</strong>{' '}
                {AutomationTriggerHumanNames[extraData.trigger]}
              </Box>
            )}
          </ModalHead>
          <ModalBody>
            {formStatus !== FormStatus.Mounted && (
              <FlexBox justifyContent="center" alignItems="center">
                <Spinner />
              </FlexBox>
            )}
            {formStatus !== FormStatus.Initial && (
              <form onSubmit={this.props.handleSubmit(this.handleConfirm)}>
                <Box>
                  <Field
                    component={FieldSelect}
                    name="matchingStrategy"
                    label="Type"
                    items={[
                      {
                        id: MatchingStrategy.ANY,
                        name: 'If any of the following conditions are met',
                      },
                      {
                        id: MatchingStrategy.ALL,
                        name: 'If all of the following conditions are met',
                      },
                    ]}
                  />
                  <br />
                  <HelperField
                    fieldId="condition"
                    label="Conditions"
                    errorMessage={validation.rules}
                    required
                  >
                    <></>
                  </HelperField>
                  <FieldArray
                    title="Conditions"
                    name="rules"
                    component={FieldRepeater}
                    rowComponent={FieldConditionComponent}
                    rowProps={{
                      variables: this.getAvailableFlowVariables(),
                      rules,
                    }}
                    divider={
                      <RepeaterDivider
                        label={
                          matchingStrategy === MatchingStrategy.ALL
                            ? 'AND'
                            : 'OR'
                        }
                      />
                    }
                    onAddRow={this.handleAddRow}
                    onMount={this.onFormMounted}
                  />
                </Box>
              </form>
            )}
          </ModalBody>
          <ModalFoot
            confirmOnClick={this.handleConfirm}
            confirmName={getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged }}
            cancelOnClick={this.closeModal}
          />
        </Box>
      );
    }
  };
};
